import { IChartProps, IStatisticsResponseModel } from "../models/chart";
import { ModelType, TimePeriod, eventType } from "../../../../../API";
import { useEffect, useMemo, useState } from "react";

import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import { DEFAULT_STATISTICS_DATE_FORMAT } from "../../system-health/common/values";
import { createChartTooltip } from "../../system-health/common/chartTooltip";
import dayjs from "dayjs";
import defaultApexOptions from "../../system-health/common/defaultApexOptions";
import { useGetStatisticsData } from "./useGetStatisticsData";
import { useTheme } from "@mui/material";

export const useAllAlertsChart = (
  timePeriod: TimePeriod,
  locations: string[] | null
): IChartProps => {
  const { loading, data, refetching, refetchChartData } = useGetStatisticsData<
    IStatisticsResponseModel[]
  >({
    eventType: eventType.allAlerts,
    models: [],
    locations,
    timePeriod,
  });

  const theme = useTheme();

  const options = defaultApexOptions(theme);

  const [chartSeries, setChartProps] = useState<
    ApexAxisChartSeries | ApexNonAxisChartSeries | undefined
  >([]);
  const [chartOptions, setChartOptions] = useState<ApexOptions>(options);

  useEffect(() => {
    const series = generateChartSeries(data as IStatisticsResponseModel[]);
    const extendedOptions = generateChartCategories(
      data as IStatisticsResponseModel[]
    );

    setChartProps(series);

    setChartOptions(extendedOptions);
  }, [data, theme.palette.mode]);

  const tooltip = useMemo(() => createChartTooltip(), []);

  const generateChartSeries = (
    response: IStatisticsResponseModel[]
  ): ApexOptions["series"] => {
    if (!Array.isArray(response) || response.length === 0) {
      return [];
    }

    const groupedByDate = response.reduce((acc, el) => {
      const dateKey = dayjs(el.date).format("YYYY-MM-DD");

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(el);

      return acc;
    }, {} as Record<string, IStatisticsResponseModel[]>);

    const labels = Object.keys(groupedByDate).sort();

    const series = Object.values(ModelType).map((model: string) => {
      const seriesData = labels.map(dateKey => {
        const entriesForDate = groupedByDate[dateKey];

        const totalForModel = entriesForDate
          .filter(entry => entry.model === model)
          .reduce((sum, entry) => sum + parseInt(entry.record_count, 10), 0);

        return totalForModel > 0 ? totalForModel : null;
      });

      return {
        name: model,
        data: seriesData,
        type:
          seriesData.filter(el => el !== null)?.length === 1
            ? "scatter"
            : "line",
      };
    });

    return series;
  };

  const generateChartCategories = (
    response: IStatisticsResponseModel[]
  ): ApexOptions => {
    if (!Array.isArray(response) || response.length === 0) {
      return options;
    }

    const groupedByDate = response.reduce((acc, el) => {
      const dateKey = dayjs(el.date).format("YYYY-MM-DD");

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(el);

      return acc;
    }, {} as Record<string, IStatisticsResponseModel[]>);

    const labels = Object.keys(groupedByDate)
      .sort()
      .map(dateKey => dayjs(dateKey).format(DEFAULT_STATISTICS_DATE_FORMAT));

    const extendedOptions: ApexOptions = {
      ...options,
      tooltip,
      stroke: {
        ...options.stroke,
        width: chartSeries?.length === 1 ? 4 : 2,
      },
      xaxis: {
        type: "datetime",
        categories: labels,
        tickAmount: timePeriod === TimePeriod.ALL_TIME ? 24 : undefined,
        tooltip: {
          enabled: false,
        },
        labels: {
          rotate: -70,
          showDuplicates: false,
          formatter: function (value: string) {
            return dayjs(value).format(DEFAULT_STATISTICS_DATE_FORMAT);
          },
        },
      },
    };

    return extendedOptions;
  };

  return {
    series: chartSeries,
    options: chartOptions,
    loading,
    refetching,
    showNoData:
      !loading && !refetching && data?.filter(d => d.record_count).length === 0,
    refetchChartData,
  };
};
